import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import DefaultCopyField from 'client/util/CopyField';
import generateToken from 'client/util/generateToken';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const TriggersModal = ({
  open,
  onClose,
  triggerIdx,
  onSave,
  triggers,
  surveyOptions,
  triggerTypes,
  delayTypes,
  messages,
  notificationToken,
  configRootUrl,
}) => {
  const { formatMessage } = useIntl();
  const editing = typeof triggerIdx === 'number';
  const triggerData = editing ? triggers[triggerIdx] : {};
  const [form, setForm] = useState({
    survey: triggerData?.survey || (surveyOptions[0] && surveyOptions[0].value) || '',
    trigger: triggerData?.trigger || triggerTypes[0]?.value || '',
    delay: triggerData?.delay || delayTypes[0] || '',
    suffix: triggerData?.suffix || '',
  });

  useEffect(() => {
    if (editing) {
      setForm({
        survey: triggerData?.survey || (surveyOptions[0] && surveyOptions[0].value) || '',
        trigger: triggerData?.trigger || triggerTypes[0]?.value || '',
        delay: triggerData?.delay || delayTypes[0] || '',
        suffix: triggerData?.suffix || '',
      });
    } else {
      setForm({
        survey: (surveyOptions[0] && surveyOptions[0].value) || '',
        trigger: triggerTypes[0]?.value || '',
        delay: delayTypes[0] || '',
        suffix: generateToken(6),
      });
    }
    // eslint-disable-next-line
  }, [open, triggerIdx, triggers]);

  const handleChange = field => event => {
    setForm(f => ({ ...f, [field]: event.target.value }));
  };

  const handleSave = () => {
    onSave({ ...form }, editing ? triggerIdx : undefined);
    onClose();
  };

  const isCustom = form.trigger === 'Custom';
  const endpointUrl =
    configRootUrl && notificationToken && form.suffix
      ? `${configRootUrl}/api/public/integrations/salesforce/notification/${notificationToken}/${form.suffix}`
      : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing
          ? formatMessage(messages.edit)
          : formatMessage(messages.addTrigger) || 'Add Trigger'}
      </DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.survey)}</InputLabel>
          <Select
            value={form.survey}
            onChange={handleChange('survey')}
            label={formatMessage(messages.survey)}
            required
          >
            {surveyOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.trigger)}</InputLabel>
          <Select
            value={form.trigger}
            onChange={handleChange('trigger')}
            label={formatMessage(messages.trigger)}
            required
          >
            {triggerTypes.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {messages[opt.label] ? formatMessage(messages[opt.label]) : opt.label}
              </MenuItem>
            ))}
          </Select>
          {isCustom && (
            <Typography variant="caption" sx={{ mb: 1 }}>
              <FormattedMessage
                id="IntegrationConfigSalesforce.CustomTrigger"
                defaultMessage={
                  'Establish personalized triggers in Salesforce for specific entity categories such as ' +
                  'Account, Case, Contact, and Opportunity by following the guidelines outlined in our {documentationLink}.'
                }
                values={{
                  documentationLink: (
                    <a
                      href="https://www.promoter.ninja/help/integrations/salesforce-integration"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FormattedMessage
                        id="IntegrationConfigSalesforce.CustomTriggerDocumentation"
                        defaultMessage="documentation"
                      />
                    </a>
                  ),
                }}
              />
            </Typography>
          )}
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.delay)}</InputLabel>
          <Select
            value={form.delay}
            onChange={handleChange('delay')}
            label={formatMessage(messages.delay)}
            required
          >
            {delayTypes.map(value => (
              <MenuItem key={value} value={value}>
                {formatMessage(messages[value])}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isCustom && !!endpointUrl && (
          <DefaultCopyField
            label={formatMessage(messages.outboundMessageUrl)}
            variant="standard"
            value={endpointUrl}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel) || 'Cancel'}</Button>
        <Button onClick={handleSave} variant="contained">
          {formatMessage(messages.save) || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TriggersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  triggerIdx: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  triggers: PropTypes.array.isRequired,
  surveyOptions: PropTypes.array.isRequired,
  triggerTypes: PropTypes.array.isRequired,
  delayTypes: PropTypes.array.isRequired,
  messages: PropTypes.object.isRequired,
  notificationToken: PropTypes.string,
  configRootUrl: PropTypes.string,
};

export default TriggersModal;
